import React, { useState, createContext, useContext } from "react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import DemoTour from "@/components/DemoTour";
import ChatWidget from "@/components/ChatWidget";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import HelperBot from "@/components/HelperBot";
import { api } from "@/lib/api";
import { canSeeRoute, INVESTOR_ALLOWED_ROUTES as INVESTOR_ALLOWED, DISTRIBUTOR_ALLOWED_ROUTES as DISTRIBUTOR_ALLOWED } from "@/lib/permissions";

const FundCtx = createContext({
  fundId: null, setFundId: () => {},
  schemeId: null, setSchemeId: () => {},
});
export const useFundContext = () => useContext(FundCtx);

export default function Shell() {
  const { user } = useAuth();
  const [fundId, setFundIdRaw] = useState(null);
  const [schemeId, setSchemeId] = useState(null);
  // Changing the fund automatically clears the scheme selection so we
  // never carry a stale scheme_id across fund boundaries.
  const setFundId = React.useCallback((nextId) => {
    setFundIdRaw(nextId);
    setSchemeId(null);
  }, []);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Auto-close mobile drawer on route change
  React.useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Page-view tracking for demo prospects — fire-and-forget so navigation
  // is never blocked by a tracker failure.
  React.useEffect(() => {
    if (!user?.is_demo || !user?.demo_id) return;
    // Skip investor/distributor role-mismatch redirects — they aren't real page views.
    api.post("/admin/demos/track/page", { path: location.pathname })
       .catch(() => { /* analytics must never break the app */ });
  }, [user?.is_demo, user?.demo_id, location.pathname]);

  // Stash the user on window for lightweight lookup from other components
  // that don't have easy access to the auth context.
  React.useEffect(() => {
    if (typeof window !== "undefined") {
      window.__AIF_USER__ = user
        ? { is_demo: !!user.is_demo, demo_id: user.demo_id }
        : null;
    }
  }, [user]);

  // Role-based redirect happens here so it triggers on every route change
  if (user?.active_role === "investor") {
    const ok = INVESTOR_ALLOWED.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));
    if (!ok) return <Navigate to="/investor" replace />;
  }
  if (user?.active_role === "distributor") {
    const ok = DISTRIBUTOR_ALLOWED.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));
    if (!ok) return <Navigate to="/distributor" replace />;
  }
  if (user && user.active_role !== "investor" && (location.pathname === "/investor" || location.pathname.startsWith("/investor/"))) {
    return <Navigate to="/dashboard" replace />;
  }
  if (user && user.active_role !== "distributor"
      && (location.pathname === "/distributor" || location.pathname.startsWith("/distributor/"))) {
    return <Navigate to="/dashboard" replace />;
  }
  // Block routes the active role is not allowed to see.
  // Platform admins (roles includes "platform_admin") retain access to /platform/*
  // regardless of their active_role, so pass the full role set down.
  if (user && user.active_role !== "investor" && user.active_role !== "distributor"
      && !canSeeRoute(user.active_role, location.pathname,
                      { roles: user.roles || [], isPlatformAdmin: !!user.is_platform_admin })
      && location.pathname !== "/dashboard") {
    return <Navigate to="/dashboard" replace />;
  }

  // LP portal gets a warmer, calmer skin than the admin console
  // (Fundclair-inspired) — driven by a single `.lp-skin` class on the outer
  // shell so the whole subtree, including cards, tables and buttons, adapts.
  const skinClass = user?.active_role === "investor" ? "lp-skin" : "";
  return (
    <FundCtx.Provider value={{ fundId, setFundId, schemeId, setSchemeId }}>
      <div className={`flex h-screen w-full bg-[#fdfcf8] overflow-hidden aif-grain ${skinClass}`}>
        {/* Sidebar: static on lg+, off-canvas drawer below lg */}
        <Sidebar activeRole={user?.active_role} isDemo={!!user?.is_demo}
                 isPlatformAdmin={!!user?.is_platform_admin} roles={user?.roles || []}
                 mobileOpen={mobileOpen} onClose={() => setMobileOpen(false)} />

        {/* Backdrop for mobile drawer */}
        {mobileOpen ? (
          <div
            className="fixed inset-0 bg-black/40 z-40 lg:hidden"
            onClick={() => setMobileOpen(false)}
            data-testid="sidebar-backdrop"
          />
        ) : null}

        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          <ImpersonationBanner />
          <Topbar fundId={fundId} setFundId={setFundId} onMenuClick={() => setMobileOpen(true)} />
          <main className="flex-1 overflow-y-auto">
            <Outlet />
          </main>
        </div>
      </div>
      {user?.is_demo && <DemoTour />}
      {user?.is_demo && <ChatWidget />}
      {/* Role-aware operational Copilot — hidden for demo prospects since the
          sales ChatWidget already occupies the corner. */}
      {user && !user?.is_demo && <HelperBot />}
    </FundCtx.Provider>
  );
}
